/**
 * Demo-harness Pearl shell.
 *
 * Functionally identical to `createPearlShell` from
 * [src/pearl-shell/pearl-shell.ts](../../src/pearl-shell/pearl-shell.ts) but
 * with two surgical differences so the demo only ever loads sandbox URLs:
 *
 *   1. `buildUrl` is imported from `./sandbox-urls` (which rewrites every
 *      hellopearl.com host to its `sandbox.*` equivalent).
 *   2. After `createLoginPopup` is constructed, its `open` method is wrapped
 *      so the embedded login iframe loads the sandbox PI URL instead of the
 *      production one. Cheap and localised — saves us copying the full
 *      ~140-line `createLoginPopup` factory.
 *
 * Every other dependency (shadow DOM, inline panel, login popup factory,
 * icon config, zoom compensation, sandbox mapper) is imported directly from
 * the unmodified extension source. Keeping this file structurally close to
 * `pearl-shell.ts` makes it obvious when a future change to the extension
 * shell needs mirroring here.
 */

import { NAV_TREE, VOICE_ACTION, type NavLeaf, type LeafAction } from '../../src/pearl-shell/icon-config';
import { applySandboxMapping } from '../../src/pms/adapters/denticon/sandbox-ids';
import { isDenticonPatientPage } from '../../src/pms/adapters/denticon/patient-page';
import { createShadowHost, type ShadowHostRefs } from '../../src/pearl-shell/shadow-dom';
import {
  applyZoomCompensation,
  scheduleZoomRetries,
  type SetTimeoutFn,
} from '../../src/pearl-shell/zoom-compensation';
import { createInlinePanel, type InlinePanel } from '../../src/pearl-shell/inline-panel';
import { createLoginPopup, type LoginPopup } from '../../src/pearl-shell/login-popup';
import type { FeatureFlags } from '../../src/services/configuration';

import { buildUrl, SANDBOX_LOGIN_URL } from './sandbox-urls';

export interface PearlShellDeps {
  getSandboxEnabled: () => Promise<boolean>;
  getInlinePanelEnabled: () => Promise<boolean>;
  getPatientOfficeIds: () => { patientId: string | null; officeId: string | null };
  getPathname: () => string;
  getHref: () => string;
  /** Returns the absolute URL to a widget SVG, keyed by path under icons/widget/ (no .svg). */
  getIconUrl: (name: string) => string;
  onVoiceClick: () => void;
  openTabInBackground: (url: string) => void;
  /** Voice iframe URL (env-specific). */
  getVoiceUrl?: () => string;
  /** Fired when the user toggles minimize via the hide button (true) or mini-tab (false). */
  onMinimizedChange?: (minimized: boolean) => void;
  /**
   * Resolves a preloaded iframe for `url` (inline preload pool), if the embedder
   * registered a preloader.
   */
  getPreloadedIframe?: (url: string) => HTMLIFrameElement | null;
  /** Optional hook when the user invokes login from the disconnected FAB. */
  onDisconnectedFabClick?: () => void;
  /** Optional overrides for testability. */
  setTimeoutFn?: SetTimeoutFn;
  windowObj?: Window;
}

export interface PearlShell {
  destroy: () => void;
  setHidden: (hidden: boolean) => void;
  setMinimized: (minimized: boolean) => void;
  isMinimized: () => boolean;
  setConnected: (connected: boolean) => void;
  isConnected: () => boolean | null;
  getPanelBody: () => HTMLElement;
  openLoginPopup: () => void;
  closeLoginPopup: () => void;
  setFeatureFlags: (flags: FeatureFlags) => void;
  getInsuranceUrlOverride: () => string | null;
}

const PLUS_SVG =
  '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">'
  + '<path d="M12 5.25v13.5M5.25 12h13.5" stroke="#050505" stroke-width="1.2" stroke-linecap="round"/>'
  + '</svg>';

const X_SVG =
  '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">'
  + '<path d="M6 6l12 12M18 6L6 18" stroke="#050505" stroke-width="1.2" stroke-linecap="round"/>'
  + '</svg>';

const CHEVRONS_RIGHT_SVG =
  '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">'
  + '<path d="M6 17l5-5-5-5M13 17l5-5-5-5" stroke="#050505" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>'
  + '</svg>';

const CHEVRON_LEFT_SVG =
  '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">'
  + '<path d="M14 6l-6 6 6 6" stroke="#050505" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>'
  + '</svg>';

const BADGE_SVG =
  '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true">'
  + '<circle cx="8" cy="8" r="8" fill="#E53935"/>'
  + '<path d="M8 4v5.5M8 12.5h.01" stroke="#FFFFFF" stroke-width="1.35" stroke-linecap="round"/>'
  + '</svg>';

/**
 * Wraps a freshly created `LoginPopup` so its iframe always points at the
 * sandbox PI URL instead of production. Mutates the returned object in place.
 */
function applySandboxLoginPopup(popup: LoginPopup): LoginPopup {
  const originalOpen = popup.open;
  popup.open = (): void => {
    originalOpen();
    const iframe = popup.getElement().querySelector('iframe') as HTMLIFrameElement | null;
    if (iframe && iframe.src !== SANDBOX_LOGIN_URL) {
      iframe.src = SANDBOX_LOGIN_URL;
    }
  };
  return popup;
}

/**
 * Demo-only Pearl shell factory. Mirrors `createPearlShell` from
 * [pearl-shell.ts](../../src/pearl-shell/pearl-shell.ts).
 *
 * @param doc - Owning document the shell mounts into.
 * @param deps - Shell dependencies; same shape as the extension's `PearlShellDeps`.
 * @returns A live `PearlShell` handle.
 */
const DEMO_CSS_OVERRIDES = `
  .pw-pnl.pw-pnl-voice .pw-pnl-body {
    width: 500px !important;
    height: 90vh !important;
    transition: width 0.3s ease, height 0.25s ease, border-radius 0.25s ease !important;
  }
  .pw-pnl.pw-pnl-voice.pw-pnl-perio .pw-pnl-body {
    width: 90vw !important;
  }
`;

export function createDemoShell(doc: Document, deps: PearlShellDeps): PearlShell {
  const refs: ShadowHostRefs = createShadowHost(doc);
  const { host, shadow, zoomWrap } = refs;

  refs.style.textContent += DEMO_CSS_OVERRIDES;

  const win: Window = deps.windowObj || doc.defaultView || (globalThis as unknown as Window);
  const setTimeoutFn: SetTimeoutFn = deps.setTimeoutFn
    || ((fn, ms) => (win as unknown as { setTimeout: SetTimeoutFn }).setTimeout(fn, ms));

  applyZoomCompensation(win as unknown as Parameters<typeof applyZoomCompensation>[0], zoomWrap);
  scheduleZoomRetries(win as unknown as Parameters<typeof scheduleZoomRetries>[0], zoomWrap, setTimeoutFn);

  const root = doc.createElement('div');
  root.className = 'pw-root';

  const stack = doc.createElement('div');
  stack.className = 'pw-icon-stack';

  const iconElements: HTMLElement[] = [];

  for (const node of NAV_TREE) {
    const ic = doc.createElement('div');
    ic.className = node.kind === 'leaf' ? 'pw-ic pw-leaf' : 'pw-ic pw-group';
    if (node.kind === 'leaf') {
      ic.setAttribute('data-action', node.action);
      if (node.hidden) ic.classList.add('pw-hidden');
    } else {
      ic.setAttribute('data-group', node.id);
    }

    const btn = doc.createElement('button');
    btn.type = 'button';
    btn.className = 'pw-ic-btn';
    btn.setAttribute('aria-label', node.label);
    if (node.kind === 'group') btn.setAttribute('aria-haspopup', 'menu');

    const img = doc.createElement('img');
    img.src = deps.getIconUrl(node.iconFile);
    img.alt = '';
    btn.appendChild(img);
    ic.appendChild(btn);

    if (node.kind === 'leaf') {
      const tip = doc.createElement('span');
      tip.className = 'pw-tip';
      tip.textContent = node.label;
      ic.appendChild(tip);

      btn.addEventListener('click', (ev) => {
        ev.stopPropagation();
        void handleAction(node.action);
      });
    } else {
      const dropdown = doc.createElement('div');
      dropdown.className = 'pw-dropdown';
      dropdown.setAttribute('role', 'menu');

      const dpanel = doc.createElement('div');
      dpanel.className = 'pw-dropdown-panel';

      const header = doc.createElement('div');
      header.className = 'pw-dropdown-header';
      header.textContent = node.label;
      dpanel.appendChild(header);

      for (const child of node.children) {
        const item = doc.createElement('button');
        item.type = 'button';
        item.className = 'pw-dropdown-item';
        item.setAttribute('role', 'menuitem');
        item.setAttribute('data-action', child.action);

        const childImg = doc.createElement('img');
        childImg.src = deps.getIconUrl(child.iconFile);
        childImg.alt = '';
        item.appendChild(childImg);

        const label = doc.createElement('span');
        label.className = 'pw-dropdown-item-label';
        label.textContent = child.label;
        item.appendChild(label);

        item.addEventListener('click', (ev) => {
          ev.stopPropagation();
          void handleAction(child.action);
        });

        dpanel.appendChild(item);
      }

      dropdown.appendChild(dpanel);
      ic.appendChild(dropdown);

      btn.addEventListener('click', (ev) => { ev.stopPropagation(); });
    }

    stack.appendChild(ic);
    iconElements.push(ic);
  }

  const fab = doc.createElement('button');
  fab.type = 'button';
  fab.className = 'pw-fab';
  fab.setAttribute('aria-label', 'Pearl');
  const fabPearl = doc.createElement('div');
  fabPearl.className = 'pw-fab-pearl';
  fabPearl.innerHTML = PLUS_SVG;
  const fabX = doc.createElement('div');
  fabX.className = 'pw-fab-x';
  fabX.innerHTML = X_SVG;
  fab.appendChild(fabPearl);
  fab.appendChild(fabX);
  const fabBadge = doc.createElement('span');
  fabBadge.className = 'pw-badge';
  fabBadge.innerHTML = BADGE_SVG;
  fab.appendChild(fabBadge);

  const hideBtn = doc.createElement('button');
  hideBtn.type = 'button';
  hideBtn.className = 'pw-hide-btn';
  hideBtn.setAttribute('aria-label', 'Minimize');
  hideBtn.innerHTML = `${CHEVRONS_RIGHT_SVG}<span class="pw-hide-tip">Minimize</span>`;

  const fabZone = doc.createElement('div');
  fabZone.className = 'pw-fab-zone';
  fabZone.appendChild(fab);
  fabZone.appendChild(hideBtn);

  root.appendChild(stack);
  root.appendChild(fabZone);

  const miniTab = doc.createElement('button');
  miniTab.type = 'button';
  miniTab.className = 'pw-mini-tab';
  miniTab.setAttribute('aria-label', 'Show Pearl');
  miniTab.innerHTML = CHEVRON_LEFT_SVG;
  const miniTabBadge = doc.createElement('span');
  miniTabBadge.className = 'pw-badge';
  miniTabBadge.innerHTML = BADGE_SVG;
  miniTab.appendChild(miniTabBadge);

  zoomWrap.appendChild(root);
  zoomWrap.appendChild(miniTab);

  const panel: InlinePanel = createInlinePanel(doc, shadow, {
    setTimeoutFn,
    getPreloadedIframe: deps.getPreloadedIframe,
  });

  const loginPopup: LoginPopup = applySandboxLoginPopup(createLoginPopup(doc, shadow, {}));

  let isOpen = false;
  let isMinimized = false;
  let isConnected: boolean | null = null;
  let insuranceUrlOverride: string | null = null;
  let leaveTimer: ReturnType<typeof setTimeout> | null = null;

  function showIcons(): void {
    if (leaveTimer) { clearTimeout(leaveTimer); leaveTimer = null; }
    for (const ic of iconElements) ic.classList.add('visible');
    fab.classList.add('open');
  }
  function hideIcons(): void {
    for (const ic of iconElements) ic.classList.remove('visible');
    fab.classList.remove('open');
  }

  root.addEventListener('mouseenter', () => {
    if (isMinimized) return;
    if (isConnected === true) showIcons();
    hideBtn.classList.add('visible');
  });
  root.addEventListener('mouseleave', () => {
    if (isMinimized) return;
    if (isConnected === true) {
      leaveTimer = setTimeoutFn(hideIcons, 120) as unknown as ReturnType<typeof setTimeout>;
    }
    hideBtn.classList.remove('visible');
  });

  fab.addEventListener('click', () => {
    if (isConnected === false) {
      if (deps.onDisconnectedFabClick) deps.onDisconnectedFabClick();
      loginPopup.open();
      return;
    }
    if (isConnected === null) return;
    if (isOpen) hideIcons(); else showIcons();
    isOpen = !isOpen;
  });

  hideBtn.addEventListener('click', (ev) => {
    ev.stopPropagation();
    minimize(true);
  });

  miniTab.addEventListener('click', () => {
    minimize(false);
  });

  function minimize(next: boolean): void {
    if (next === isMinimized) return;
    isMinimized = next;
    if (next) {
      hideIcons();
      hideBtn.classList.remove('visible');
      isOpen = false;
      fab.style.transition = 'transform 0.4s cubic-bezier(0.4,0,1,1), opacity 0.3s ease';
      fab.style.transform = 'translateX(80px)';
      fab.style.opacity = '0';
      setTimeoutFn(() => {
        fab.style.display = 'none';
        root.style.right = '-100px';
        miniTab.style.display = 'flex';
        setTimeoutFn(() => miniTab.classList.add('visible'), 0);
      }, 380);
    } else {
      miniTab.classList.remove('visible');
      setTimeoutFn(() => { miniTab.style.display = 'none'; }, 280);
      setTimeoutFn(() => {
        root.style.right = '25px';
        fab.style.display = '';
        fab.style.opacity = '0';
        fab.style.transform = 'translateX(80px)';
        setTimeoutFn(() => {
          fab.style.transition = 'transform 0.5s cubic-bezier(0.2,2.2,0.4,1), opacity 0.3s ease';
          fab.style.transform = 'translateX(0)';
          fab.style.opacity = '1';
        }, 0);
      }, 200);
    }
    if (deps.onMinimizedChange) deps.onMinimizedChange(next);
  }

  function setMinimized(next: boolean): void {
    if (next === isMinimized) return;
    isMinimized = next;
    if (next) {
      hideIcons();
      hideBtn.classList.remove('visible');
      isOpen = false;
      fab.style.display = 'none';
      root.style.right = '-100px';
      miniTab.style.display = 'flex';
      miniTab.classList.add('visible');
    } else {
      fab.style.display = '';
      fab.style.transform = '';
      fab.style.opacity = '';
      root.style.right = '25px';
      miniTab.classList.remove('visible');
      miniTab.style.display = 'none';
    }
  }

  /**
   * Routes a leaf action — uses the SANDBOX `buildUrl` from `./sandbox-urls`
   * so every URL is rewritten to its `sandbox.*` equivalent before the
   * inline panel or new-tab opener sees it.
   *
   * Voice is treated like any other action here (unlike the production shell
   * which delegates to `onVoiceClick` for the dedicated voice-iframe
   * lifecycle). In the demo we simply open it in the inline panel's
   * bottom-right floating variant, same as the production `panel.open(url, true)`.
   */
  async function handleAction(action: LeafAction): Promise<void> {
    const [sandboxEnabled, inlineEnabled] = await Promise.all([
      deps.getSandboxEnabled(),
      deps.getInlinePanelEnabled(),
    ]);
    if (!isConnected && inlineEnabled) return;
    const rawIds = deps.getPatientOfficeIds();
    const ids = applySandboxMapping(rawIds, sandboxEnabled);
    const pathname = deps.getPathname();
    const href = deps.getHref();
    const url = buildUrl(action, {
      patientId: ids.patientId,
      officeId: ids.officeId,
      pathname,
      href,
      onPatientPage: isDenticonPatientPage(pathname, href),
      insuranceUrlOverride: insuranceUrlOverride ?? undefined,
      voiceUrl: deps.getVoiceUrl?.(),
    });
    if (!url) return;

    hideIcons();
    isOpen = false;
    if (inlineEnabled) {
      panel.open(url, action === VOICE_ACTION);
    } else {
      deps.openTabInBackground(url);
    }
  }

  function setFeatureFlags(flags: FeatureFlags): void {
    const actionMap: Record<string, keyof FeatureFlags> = {
      tooth: 'sodActive',
      treatments: 'piActive',
      insurance: 'insuranceActive',
      voice: 'voiceActive',
    };
    const groupMap: Record<string, keyof FeatureFlags> = {
      practice: 'piActive',
      insights: 'piActive',
    };

    for (const ic of iconElements) {
      const action = ic.getAttribute('data-action');
      const group = ic.getAttribute('data-group');
      let flag: keyof FeatureFlags | undefined;
      if (action && actionMap[action]) flag = actionMap[action];
      else if (group && groupMap[group]) flag = groupMap[group];
      if (flag !== undefined) {
        if (flags[flag]) ic.classList.remove('pw-hidden');
        else ic.classList.add('pw-hidden');
      }
    }

    insuranceUrlOverride =
      !flags.piActive && flags.insuranceActive
        ? 'https://sandbox.precheck.hellopearl.com/'
        : null;
  }

  function setConnected(connected: boolean): void {
    isConnected = connected;
    if (connected) {
      zoomWrap.classList.remove('pw-disconnected');
    } else {
      zoomWrap.classList.add('pw-disconnected');
    }
  }

  function destroy(): void {
    loginPopup.destroy();
    panel.destroy();
    if (host.parentNode) host.parentNode.removeChild(host);
  }

  function setHidden(hidden: boolean): void {
    host.style.display = hidden ? 'none' : '';
  }

  void (null as unknown as NavLeaf);

  return {
    destroy,
    setHidden,
    setMinimized,
    isMinimized: () => isMinimized,
    setConnected,
    isConnected: () => isConnected,
    getPanelBody: () => panel.getBody(),
    openLoginPopup: () => loginPopup.open(),
    closeLoginPopup: () => loginPopup.close(),
    setFeatureFlags,
    getInsuranceUrlOverride: () => insuranceUrlOverride,
  };
}
